import { useContext, useEffect } from "react";

import { Box } from "@mui/material";
import {
  _ACTIONS,
  _COLORS,
  _HTTP,
  _MESSAGES,
  _ROLES,
  _ROLES_BACK,
  _SWAL_ICONS,
} from "../../../utils/constants";
import { GlobalContext } from "../../../contexts/GlobalContext";
import { useMaterialReactTable } from "material-react-table";
import { UsersListColumns } from "../../../utils/MuiTableUtils/ColumnBuilders/UsersListColumns";
import { getMuiTableConfig } from "../../../utils/MuiTableUtils/muiTableBuilder";
import BasicModal from "../../helpers/BasicModal";
import { useFormik } from "formik";
import { array, bool, object, setLocale, string } from "yup";
import PersonIcon from "@mui/icons-material/Person";
import { UserContext } from "../../../contexts/UserContext";
import { _ROUTES } from "../../../utils/_routes_constants";
import { RequestHelper } from "../../../utils/requestHelper";
import { en } from "yup-locales";
import { BasicFormikForm } from "../../form/BasicFormikForm";
import { BasicListContext } from "../../../contexts/BasicListContext";
import { MuiTableComponent } from "../../helpers/MuiTableComponent";
import { createToast } from "../../../utils/toast";

export default function UsersList({
  editData,
  setEditData,
  showModal,
  setShowModal,
}) {
  const { open } = useContext(GlobalContext);
  const { user } = useContext(UserContext);
  const v = object({
    email: string().email().required().label("Mail"),
    firstName: string().required().label("First Name"),
    lastName: string().required().label("Last Name"),
    active: bool().required().label("Active"),
    roles: array()
      .min(1, "Veuillez choisir au moins un rôle")
      .label("User Profile"),
  });

  setLocale(en);

  const formik = useFormik({
    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
      active: true,
      roles: [],
    },
    onSubmit: (values) => {
      const methode = editData ? _ACTIONS.PATCH : _ACTIONS.POST;
      const url = _ROUTES.USERS + (editData ? `/${editData.id}` : "");
      const user = {
        email: values.email,
        roles: values.roles,
        firstName: values.firstName,
        lastName: values.lastName,
        active: values.active,
      };

      const rq = new RequestHelper();

      rq.doRequest(methode, url, user)
        .then((res) => {
          if (res.status === _HTTP.created || res.status === _HTTP.ok) {
            setShowModal(false);
            setEditData(null);
            setRefresh((prev) => {
              return prev + 1;
            });
            createToast(_SWAL_ICONS.success, "User saved successfully");
          }
        })
        .catch((err) => {
          createToast(_SWAL_ICONS.error, _MESSAGES.error);
        });
    },
    validationSchema: v,
  });

  formik["special"] = {
    placeholder: {
      email: "adress@mail.com",
      firstName: "John",
      lastName: "Doe",
      roles: "Select one role or more",
    },
  };

  const {
    rowCount,
    data,
    isLoaded,
    isError,
    pagination,
    setPagination,
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    sorting,
    setSorting,
    isLoading,
    setRefresh,
  } = useContext(BasicListContext);

  // Get the table configuration
  const c = getMuiTableConfig(
    // Params
    { enableColumnFilters: true, enableExpanding: true },
    // States & setStates (from context) Have to be the same as this
    {
      rowCount,
      isLoaded,
      isError,
      pagination,
      setPagination,
      columnFilters,
      setColumnFilters,
      globalFilter,
      setGlobalFilter,
      sorting,
      setSorting,
      isLoading,
    }
  );

  const columns = UsersListColumns({
    setEditData,
    setShowModal,
    user,
    setRefresh,
  });

  const table = useMaterialReactTable({
    columns: columns,
    data: data,
    ...c,
    enableColumnResizing: false,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    initialState: {
      density: "compact",
    },
  });

  useEffect(() => {
    if (editData) {
      formik.setValues({
        email: editData.email,
        roles: editData.roles.filter((role) => role !== _ROLES_BACK._ROLE_USER),
        firstName: editData.firstName,
        lastName: editData.lastName,
        active: editData.active,
      });
    } else {
      formik.resetForm();
    }
  }, [editData]);

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: open ? window.innerWidth - 340 : window.innerWidth - 64,
          borderRadius: "1px",
          boxShadow: `0px 0px 2px ${_COLORS.grey} `,
          overflowX: "auto",
        }}>
        {/* Table Component is here */}
        <MuiTableComponent route={_ROUTES.USERS} table={table} />
      </Box>
      {/* Modal  */}
      <BasicModal
        showModal={showModal}
        setShowModal={setShowModal}
        title={"User Form"}
        content={
          <BasicFormikForm
            formik={formik}
            validations={v}
            options={{
              roles: _ROLES,
            }}
            setEditData={setEditData}
            setShowModal={setShowModal}
          />
        }
        icon={<PersonIcon />}
        width={"500"}
      />
    </>
  );
}
